import PropTypes from 'prop-types';
import { createRef, useEffect, useMemo, useState } from 'react';
import { Trans, withTranslation } from 'react-i18next';

import { trackAction } from '@ctct/distui-helpers';
import { Button, Dialog, Icon, Spinner } from '@ctct/ui-react';

import MyAccountApiLite from '../../api/MyAccountApiLite';
import { PremiumPlanIds } from '../../constants/planIds';
import SMSTiersCard from '../SMSTierDialogWithOrderSummaryV2/SMSTiersCard';
import SMSTiersTable from '../SMSTierDialogWithOrderSummaryV2/SMSTiersTable/SMSTiersTable';

const { getAddOns } = MyAccountApiLite;

const SMSTierDialogV2 = ({
  t,
  open,
  closeDialog,
  initiallySelectedSMSTier,
  currency,
  SMSTiers,
  saveSmsSelection,
  addSmsButtonText,
  rating,
  contactCount,
}) => {
  const [showAllTiers, setShowAllTiers] = useState(0);
  const viewMoreRef = createRef();
  const [smsSelection, setSmsSelection] = useState({ min: 501, max: 1000, price: 20 });

  const { paymentPeriod, monthlyPrice, prepayTotalAmountSaved, ratePlans } = rating || {};
  const [ratePlan] = ratePlans || [];
  const { planId } = ratePlan || {};
  const isPremium = planId && PremiumPlanIds.includes(planId);

  const prepayDiscountPercentage = useMemo(() => {
    return paymentPeriod > 1 ? 1 - prepayTotalAmountSaved / (paymentPeriod * monthlyPrice + prepayTotalAmountSaved) : 0;
  }, [paymentPeriod, monthlyPrice, prepayTotalAmountSaved]);

  const showCardDecimals = useMemo(() => {
    if (SMSTiers && SMSTiers.length > 0) {
      for (let i = 0; i < 3; i += 1) {
        let retVal = new Intl.NumberFormat({
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format((SMSTiers[i].price * 100 * prepayDiscountPercentage) % 100);

        if (retVal.length === 1) {
          retVal = `0${retVal}`;
        }

        if (retVal !== '00') {
          return true;
        }
      }
    }

    return false;
  }, [SMSTiers, prepayDiscountPercentage]);

  useEffect(() => {
    if (showAllTiers && SMSTiers && viewMoreRef && viewMoreRef.current) {
      setTimeout(() => {
        viewMoreRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 1);
    }
  }, [showAllTiers, SMSTiers]);

  useEffect(() => {
    trackAction({ action: 'sms-dialog-view' });
    (async () => {
      // If it's premium plan, select first tier by default
      if (SMSTiers && SMSTiers.length > 0) {
        if (isPremium) {
          const addOns = await getAddOns(currency, planId);
          const smsAddOn = addOns.find((ao) => ao.id === 'CTCT-SMS-ADDON');

          if (smsAddOn && smsAddOn.included) {
            const { tierMin, tierMax, price } = SMSTiers[0];

            setSmsSelection({ min: tierMin, max: tierMax, price });
          }
        } else {
          const { tierMin, tierMax, price } = SMSTiers[1];

          setSmsSelection({ min: tierMin, max: tierMax, price });
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (open) {
      setShowAllTiers(0);
    }
  }, [open]);

  // TODO add actual learn more URL @61
  return (
    <Dialog
      heading={t('sms-plans.name')}
      data-qe-id="sms-plans-dialog"
      contentFlush={false}
      kind="standard"
      isOpen={open}
      onDismiss={closeDialog}
      footerActions={
        <div style={{ width: '100%', display: 'flex', flexDirection: 'row-reverse', alignItems: 'center' }}>
          <div>
            <Button
              block
              className="sidebar-continue-button"
              kind="primary"
              data-qe-id="sms-plans-dialog-continue-button"
              onClick={() => {
                trackAction({ action: 'add-sms-button-select' });
                saveSmsSelection(smsSelection);
                closeDialog();
              }}
              text={addSmsButtonText || t('sms-plans.add-sms')}
            />
          </div>
          <div style={{ marginRight: 2 }}>
            <Button
              block
              className="sidebar-continue-button"
              kind="secondary"
              data-qe-id="sms-plans-dialog-cancel-button"
              onClick={() => {
                setSmsSelection(initiallySelectedSMSTier);
                closeDialog();
              }}
              text={t('sms-plans.cancel')}
            />
          </div>
        </div>
      }
    >
      <div style={{ maxHeight: 560 }}>
        <header>
          <div style={{ marginBottom: 20, width: '100%' }}>
            <span style={{ fontSize: 20, fontWeight: 600, lineHeight: '28px' }}>
              {t('sms-plans.dialog.how-many-texts')}
            </span>
          </div>
          <div style={{ marginBottom: 16 }}>
            <Trans i18nKey="sms-plans.dialog.pick-starting-price">
              <a
                href="https://knowledgebase.constantcontact.com/articles/KnowledgeBase/45188-About-Constant-Contacts-SMS-tool?lang=en_US"
                target="_blank"
                rel="noopener noreferrer"
              >
                some content
              </a>
            </Trans>
          </div>
        </header>
        <div>
          {!SMSTiers || SMSTiers.length === 0 || prepayDiscountPercentage == null ? (
            <div style={{ padding: 160, display: 'flex', justifyContent: 'center' }}>
              <Spinner size="large" />
            </div>
          ) : (
            <>
              <SMSTiersCard
                smsTier={SMSTiers[0]}
                prepayDiscountPercentage={prepayDiscountPercentage}
                selectedSMSTier={smsSelection}
                selectTierCallback={(min, max, price) => {
                  trackAction({ action: 'stp-sms-tier-select' });
                  setSmsSelection({ min, max, price });
                }}
                currency={currency}
                description={t('sms-plans.dialog.tier-1-description')}
                showCardDecimals={showCardDecimals}
                maxTextWidth="auto"
                isPremium={isPremium}
              />
              <SMSTiersCard
                smsTier={SMSTiers[1]}
                prepayDiscountPercentage={prepayDiscountPercentage}
                selectedSMSTier={smsSelection}
                selectTierCallback={(min, max, price) => {
                  trackAction({ action: 'stp-sms-tier-select' });
                  setSmsSelection({ min, max, price });
                }}
                currency={currency}
                description={t('sms-plans.dialog.tier-2-description')}
                showCardDecimals={showCardDecimals}
                maxTextWidth="auto"
              />
              <SMSTiersCard
                smsTier={SMSTiers[2]}
                prepayDiscountPercentage={prepayDiscountPercentage}
                selectedSMSTier={smsSelection}
                selectTierCallback={(min, max, price) => {
                  trackAction({ action: 'stp-sms-tier-select' });
                  setSmsSelection({ min, max, price });
                }}
                currency={currency}
                description={t('sms-plans.dialog.tier-3-description')}
                showCardDecimals={showCardDecimals}
                maxTextWidth="auto"
              />
            </>
          )}
        </div>
        <div>
          {!SMSTiers || SMSTiers.length === 0 || prepayDiscountPercentage == null ? (
            <div style={{ padding: 160, display: 'flex', justifyContent: 'center' }}>
              <Spinner size="large" />
            </div>
          ) : (
            <div className="tiers-content" ref={viewMoreRef}>
              <div style={{ textAlign: 'center', fontSize: 14, lineHeight: '20px', fontWeight: 600, marginBottom: -6 }}>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowAllTiers(((showAllTiers + 1) % 100) + 1);
                  }}
                >
                  {`${t('sms-plans.dialog.view-more-options')}  `}
                  {showAllTiers > 0 && (!SMSTiers || SMSTiers.length === 0) ? (
                    <Spinner />
                  ) : (
                    <Icon style={{ textDecoration: 'none', color: '#1856ED' }} glyph="chevronSouth" />
                  )}
                </a>
              </div>
              {showAllTiers > 0 && (
                <div style={{ paddingTop: 22 }}>
                  <SMSTiersTable
                    currency={currency}
                    selectedSMSTier={smsSelection}
                    prepayDiscountPercentage={prepayDiscountPercentage}
                    t={t}
                    setSelectedSMSTier={(min, max, price) => {
                      trackAction({ action: 'stp-sms-tier-select' });
                      setSmsSelection({ min, max, price });
                    }}
                    // eslint-disable-next-line no-nested-ternary
                    SMSTiers={showAllTiers ? (SMSTiers && SMSTiers.length ? SMSTiers.slice(3) : []) : []}
                    contactCountLowEnd={contactCount}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

SMSTierDialogV2.defaultProps = {
  addSmsButtonText: null,
};

SMSTierDialogV2.propTypes = {
  t: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  closeDialog: PropTypes.func.isRequired,
  saveSmsSelection: PropTypes.func.isRequired,
  currency: PropTypes.string.isRequired,
  initiallySelectedSMSTier: PropTypes.shape({
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    price: PropTypes.number.isRequired,
  }).isRequired,
  SMSTiers: PropTypes.arrayOf(
    PropTypes.shape({
      tierMin: PropTypes.number.isRequired,
      tierMax: PropTypes.number.isRequired,
      price: PropTypes.number.isRequired,
    })
  ).isRequired,
  addSmsButtonText: PropTypes.string,
  rating: PropTypes.oneOfType([PropTypes.object]).isRequired,
  contactCount: PropTypes.number.isRequired,
};

export default withTranslation()(SMSTierDialogV2);
